import { useTranslation } from "react-i18next";
import { CrownIcon } from "@/components/Icons";
import PlanComparison from "@/components/onboarding/PlanComparison";
import type { OnboardingIntent, Persona } from "@/models/onboardingPersona";
import { getStoreProofTranslationValues } from "@/product-proof";

// Native onboarding and desktop upgrades share the same intent-led benefits.
const PaywallBenefits = ({
  intent,
  persona,
  setupReady = false,
}: {
  intent: OnboardingIntent | null;
  persona: Persona | null;
  setupReady?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const storeProof = getStoreProofTranslationValues(
    i18n.resolvedLanguage || i18n.language,
  );
  const personaSuffix = persona || "other";
  // Spec bullets — intent-keyed first (the job the user picked), then
  // persona-flavored, then "_other" (defensive for future personas).
  const bulletKeys = [
    ...(intent ? [`onboarding.paywall.specs_${intent}`] : []),
    `onboarding.paywall.features_${personaSuffix}`,
    "onboarding.paywall.features_other",
  ];
  let paywallFeatures: string[] = [];
  for (const key of bulletKeys) {
    const raw = t(key, { returnObjects: true }) as string[] | string;
    if (Array.isArray(raw)) {
      paywallFeatures = raw;
      break;
    }
  }
  paywallFeatures = paywallFeatures.slice(0, 4);
  return (
    <div className="flex w-full max-w-[520px] flex-col items-center text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-tr from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] shadow-lg shadow-[var(--premium-cta-glow)]">
        <CrownIcon size={21} className="text-white" />
      </div>
      {/* The eyebrow identifies the decision without adding another
          paragraph above the intent-specific benefit headline. */}
      {setupReady && (
        <span className="mb-2 rounded-full bg-[var(--accent)]/10 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[var(--accent)]">
          {t("onboarding.paywall.setupReady")}
        </span>
      )}
      <h2 className="text-2xl font-black text-[var(--text-primary)] text-center tracking-tight leading-tight mb-2 max-w-[92%]">
        {t(
          intent
            ? `onboarding.paywall.headline_intent_${intent}`
            : `onboarding.paywall.headline_${personaSuffix}`,
        )}
      </h2>
      <p className="mb-3 text-[13px] leading-snug text-[var(--text-secondary)]">
        {t("onboarding.paywall.subtitle")}
      </p>
      <div className="mb-3 mt-1 flex w-full flex-col gap-1.5">
        {paywallFeatures.map((feature, index) => (
          <div key={index} className="flex items-center justify-center p-0">
            <span className="text-center text-[13.5px] font-medium leading-snug text-[var(--text-primary)]/90">
              {feature}
            </span>
          </div>
        ))}
      </div>
      <PlanComparison />
      <div className="glass mt-3 flex items-center gap-2 rounded-full px-3.5 py-1.5 backdrop-blur-sm">
        <span className="text-[11px] font-bold text-[var(--text-primary)]">
          {t("onboarding.paywall.trustLine")}
        </span>
        <span className="text-[var(--text-tertiary)]">·</span>
        <span className="text-[11px] font-bold text-[var(--text-primary)]">
          {t("alertWidgetPaywall.rating", storeProof)}
        </span>
      </div>
    </div>
  );
};

export default PaywallBenefits;
